import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/api/client";
import { Plus, TrendUp, TrendDown, X, DownloadSimple, PencilSimple, Trash, ChartLineUp, Buildings, Printer, Copy, ArrowsClockwise, Pause, Play, ArrowsLeftRight } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { exportCSV } from "@/utils/exporters";
import { CustomFieldsInputs } from "@/components/CustomFieldsManager";
import { useConfirm } from "@/context/ConfirmContext";
import { listFYs, currentFY, printHtml, currency } from "@/lib/printDoc";

const INCOME_CATS = ["Booking", "Customer Payment", "Rental Income", "Refund Received", "Interest", "Other Income"];
const EXPENSE_CATS = ["Material Purchase", "Contractor", "Labour", "Consultants", "Equipment", "Government Fees", "Fuel", "Electricity", "Infrastructure", "EMI / Loan", "Maintenance", "Salary", "Marketing", "Rent", "Insurance", "Petty Cash", "Other"];
const STATUSES = ["Paid", "Pending", "Partially Paid", "Overdue"];

export default function Finance() {
  const [tab, setTab] = useState("entries"); // entries | recurring
  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="finance-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">Money Out</div>
          <h1 className="font-display text-3xl font-bold text-neutral-900 mt-1">Expenses &amp; Purchases</h1>
          <div className="text-sm text-neutral-500 mt-1">Indian Financial Year (April – March)</div>
        </div>
      </div>
      <div className="border-b border-neutral-200 flex gap-6 mb-6">
        <button data-testid="fin-tab-entries" onClick={() => setTab("entries")} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${tab === "entries" ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>Entries</button>
        <button data-testid="fin-tab-recurring" onClick={() => setTab("recurring")} className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${tab === "recurring" ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}><ArrowsClockwise size={14} /> Recurring</button>
      </div>
      {tab === "entries" && <EntriesTab />}
      {tab === "recurring" && <RecurringTab />}
    </div>
  );
}

function EntriesTab() {
  const [entries, setEntries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [type, setType] = useState("all");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [fyStart, setFyStart] = useState(currentFY().start);
  const [companyFilter, setCompanyFilter] = useState("all");
  const confirm = useConfirm();
  const fys = useMemo(() => listFYs(5), []);

  const load = async () => {
    const params = new URLSearchParams();
    if (fyStart !== "all") params.set("fy_start", String(fyStart));
    if (companyFilter !== "all") params.set("company_id", companyFilter);
    const [e, c] = await Promise.all([api.get(`/finance?${params.toString()}`), api.get("/companies")]);
    setEntries(e.data); setCompanies(c.data);
  };
  useEffect(() => { load(); }, [fyStart, companyFilter]);

  const filtered = type === "all" ? entries : entries.filter((e) => e.type === type);
  const income = entries.filter((e) => e.type === "income").reduce((s, e) => s + e.amount, 0);
  const expense = entries.filter((e) => e.type === "expense").reduce((s, e) => s + e.amount, 0);
  const tdsReceivable = entries.filter((e) => e.type === "income").reduce((s, e) => s + (e.tds_amount || 0), 0);
  const tdsPayable    = entries.filter((e) => e.type === "expense").reduce((s, e) => s + (e.tds_amount || 0), 0);
  const gstOutput     = entries.filter((e) => e.type === "income").reduce((s, e) => s + (e.gst_amount || 0), 0);
  const gstInput      = entries.filter((e) => e.type === "expense").reduce((s, e) => s + (e.gst_amount || 0), 0);
  const netGst        = gstOutput - gstInput;
  const netTds        = tdsReceivable - tdsPayable;
  const bookProfit    = income - expense;
  const profitAfterTax = bookProfit - netGst + netTds;
  const currentFyLabel = fys.find((f) => f.start === fyStart)?.label || "All periods";

  const toggleStatus = async (e) => {
    const nextStatus = e.status === "Paid" ? "Pending" : "Paid";
    try {
      await api.patch(`/finance/${e.id}`, { status: nextStatus, paid_amount: nextStatus === "Paid" ? e.amount : 0 });
      load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const copyEntry = (e) => {
    // Duplicate this entry as a NEW row (no id) — user edits & saves.
    const { id, created_at, created_by, _id, ...rest } = e; // eslint-disable-line no-unused-vars
    setEdit({ ...rest, date: new Date().toISOString().slice(0, 10) });
    setOpen(true);
  };
  const del = async (e) => {
    const ok = await confirm({ title: "Delete finance entry?", message: `${e.type.toUpperCase()} · ${e.category} · ₹${e.amount.toLocaleString()}. This cannot be undone.`, confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/finance/${e.id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Financial Year</label>
        <select data-testid="finance-fy" value={fyStart} onChange={(e) => setFyStart(e.target.value === "all" ? "all" : parseInt(e.target.value, 10))} className="dos-input" style={{ maxWidth: 160 }}>
          <option value="all">All periods</option>
          {fys.map((f) => <option key={f.start} value={f.start}>{f.label}</option>)}
        </select>
        <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 ml-3 inline-flex items-center gap-1"><Buildings size={12} /> Company</label>
        <select data-testid="finance-company" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} className="dos-input" style={{ maxWidth: 220 }}>
          <option value="all">All Companies</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="ml-auto flex gap-2">
          <button data-testid="fin-export" onClick={() => exportCSV(`finance-${currentFyLabel.replace(/ /g, "_")}`, [
            { label: "Date", value: "date" }, { label: "Type", value: "type" },
            { label: "Category", value: "category" }, { label: "Party", value: "party" },
            { label: "Company", value: (e) => companies.find((c) => c.id === e.company_id)?.name || "" },
            { label: "Amount", value: "amount" }, { label: "Paid", value: "paid_amount" },
            { label: "TDS", value: "tds_amount" }, { label: "GST", value: "gst_amount" }, { label: "Status", value: "status" },
          ], entries)} className="dos-btn dos-btn-secondary text-xs"><DownloadSimple size={12} /> Export CSV</button>
          <button data-testid="new-entry-btn" onClick={() => { setEdit(null); setOpen(true); }} className="dos-btn dos-btn-primary"><Plus size={14} /> New Entry</button>
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4">Showing <span className="font-mono">{currentFyLabel}</span> · {filtered.length} rows</div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <SummaryCard label="Income" value={income} icon={TrendUp} tone="green" onClick={() => setType("income")} testId="fin-income" />
        <SummaryCard label="Expense" value={expense} icon={TrendDown} tone="red" onClick={() => setType("expense")} testId="fin-expense" />
        <SummaryCard label="Book Profit" value={bookProfit} tone={bookProfit >= 0 ? "green" : "red"} onClick={() => setType("all")} testId="fin-profit" />
        <SummaryCard label="Profit After Tax" value={profitAfterTax} tone={profitAfterTax >= 0 ? "green" : "red"} onClick={() => setType("all")} testId="fin-profit-after-tax" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <MiniStat label="TDS Receivable" value={tdsReceivable} tone="blue" testId="fin-tds-recv" />
        <MiniStat label="TDS Payable" value={tdsPayable} tone="amber" testId="fin-tds-pay" />
        <MiniStat label="Net TDS" value={netTds} tone={netTds >= 0 ? "green" : "red"} testId="fin-tds-net" />
        <MiniStat label="GST Output" value={gstOutput} tone="amber" testId="fin-gst-out" />
        <MiniStat label="GST Input" value={gstInput} tone="blue" testId="fin-gst-in" />
        <MiniStat label="Net GST Liability" value={netGst} tone={netGst >= 0 ? "red" : "green"} testId="fin-gst-net" />
      </div>

      <div className="flex items-center gap-2 mb-4">
        {["all", "income", "expense"].map((t) => (
          <button key={t} onClick={() => setType(t)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${type === t ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100"}`} data-testid={`filter-${t}`}>{t === "all" ? "All" : t[0].toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      <div className="dos-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
              <th className="px-4 py-3">Date</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Party</th><th className="px-4 py-3">Company A/c</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-right">TDS</th><th className="px-4 py-3 text-right">GST</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50" data-testid={`fin-row-${e.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-neutral-500">{e.date}</td>
                <td className="px-4 py-3"><span className={`dos-badge ${e.type === "income" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{e.type}</span></td>
                <td className="px-4 py-3 text-neutral-800">{e.category}</td>
                <td className="px-4 py-3 text-neutral-600">{e.party || "—"}</td>
                <td className="px-4 py-3 text-neutral-600 text-xs">{companies.find((c) => c.id === e.company_id)?.name || "—"}</td>
                <td className="px-4 py-3 text-right font-mono text-neutral-900">₹{e.amount.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-mono text-neutral-500 text-xs">{e.tds_amount ? `₹${e.tds_amount.toLocaleString()}` : "—"}</td>
                <td className="px-4 py-3 text-right font-mono text-neutral-500 text-xs">{e.gst_amount ? `₹${e.gst_amount.toLocaleString()}` : "—"}</td>
                <td className="px-4 py-3">
                  <button data-testid={`toggle-status-${e.id}`} onClick={() => toggleStatus(e)} title="Toggle Paid / Pending" className={`dos-badge cursor-pointer ${e.status === "Paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{e.status}</button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`copy-fin-${e.id}`} onClick={() => copyEntry(e)} title="Copy & repeat this entry" className="p-1.5 rounded hover:bg-neutral-100"><Copy size={14} /></button>
                  <button data-testid={`edit-fin-${e.id}`} onClick={() => { setEdit(e); setOpen(true); }} className="p-1.5 rounded hover:bg-neutral-100"><PencilSimple size={14} /></button>
                  <button data-testid={`del-fin-${e.id}`} onClick={() => del(e)} className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash size={14} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={10} className="p-12 text-center text-neutral-500 text-sm">No entries in this period.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <EntryModal record={edit} companies={companies} onClose={() => { setOpen(false); setEdit(null); }} onSaved={() => { setOpen(false); setEdit(null); load(); }} />}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, tone, onClick, testId }) {
  const toneCls = tone === "green" ? "text-emerald-600 bg-emerald-50" : tone === "red" ? "text-red-600 bg-red-50" : "text-neutral-600 bg-neutral-100";
  return (
    <button onClick={onClick} className="dos-card p-5 text-left hover:border-neutral-400 transition-colors duration-200" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
        {Icon && <div className={`w-8 h-8 rounded-md flex items-center justify-center ${toneCls}`}><Icon size={16} weight="bold" /></div>}
      </div>
      <div className="font-display text-2xl font-bold text-neutral-900 mt-2">₹{value.toLocaleString()}</div>
    </button>
  );
}

function EntryModal({ record, companies, onClose, onSaved }) {
  // If `record` has no id, treat it as a NEW pre-filled entry (used by the copy-and-repeat action).
  const isNew = !record?.id;
  const [type, setType] = useState(record?.type || "expense");
  const [form, setForm] = useState(record || {
    company_id: companies[0]?.id || "",
    category: EXPENSE_CATS[0], amount: "", paid_amount: "",
    tds_amount: "", gst_amount: "",
    date: new Date().toISOString().slice(0, 10), party: "", status: "Paid", description: "",
  });
  const [custom, setCustom] = useState(record?.custom || {});
  // Recurring template toggle (only meaningful on brand-new entries — not when editing an existing row)
  const [makeRecurring, setMakeRecurring] = useState(false);
  const [cadence, setCadence] = useState("monthly");

  const cats = type === "income" ? INCOME_CATS : EXPENSE_CATS;
  useEffect(() => { if (!record) setForm((f) => ({ ...f, category: cats[0] })); }, [type]); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form, type, scope: "company",
      amount: parseFloat(form.amount) || 0,
      paid_amount: parseFloat(form.paid_amount) || 0,
      tds_amount: parseFloat(form.tds_amount) || 0,
      gst_amount: parseFloat(form.gst_amount) || 0,
      custom,
    };
    try {
      if (isNew) await api.post("/finance", payload);
      else await api.patch(`/finance/${record.id}`, payload);
      // If the user opted-in to "recurring", also create a template so future periods auto-draft.
      if (isNew && makeRecurring) {
        const day = parseInt((form.date || "").slice(8, 10), 10) || 1;
        await api.post("/finance/templates", {
          name: `${form.category}${form.party ? " · " + form.party : ""}`,
          type, scope: "company",
          company_id: form.company_id || "",
          category: form.category,
          amount: payload.amount,
          party: form.party || "",
          description: form.description || "",
          tds_amount: payload.tds_amount,
          gst_amount: payload.gst_amount,
          cadence,
          day_of_month: day,
          is_active: true,
          custom,
        });
        toast.success("Recurring template saved — will auto-draft each period");
      } else {
        toast.success(isNew ? (record ? "Entry copied — new record saved" : "Entry saved") : "Entry updated");
      }
      onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 overflow-y-auto p-4 flex items-start justify-center" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-lg my-8 fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="font-display font-semibold text-neutral-900">{isNew ? (record ? "Copy & Save as New Entry" : "New Finance Entry") : "Edit Finance Entry"}</div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex gap-2">
            <button type="button" onClick={() => setType("income")} data-testid="type-income" className={`flex-1 py-2 rounded-md text-sm font-medium ${type === "income" ? "bg-emerald-500 text-white" : "bg-neutral-100 text-neutral-600"}`}>Income</button>
            <button type="button" onClick={() => setType("expense")} data-testid="type-expense" className={`flex-1 py-2 rounded-md text-sm font-medium ${type === "expense" ? "bg-red-500 text-white" : "bg-neutral-100 text-neutral-600"}`}>Expense</button>
          </div>
          <div>
            <label className="text-xs text-neutral-500">Company Account {type === "income" ? "(where received)" : "(paid from)"}</label>
            <select data-testid="entry-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className="dos-input mt-1">
              <option value="">— None —</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}{c.bank_account ? ` · ${c.bank_account}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-neutral-500">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="dos-input mt-1">
              {cats.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs text-neutral-500">Amount</label><input data-testid="entry-amount" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="dos-input mt-1" /></div>
            <div><label className="text-xs text-neutral-500">Amount Paid</label><input data-testid="entry-paid" type="number" step="0.01" value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} className="dos-input mt-1" /></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="text-xs text-neutral-500">TDS Deducted</label><input data-testid="entry-tds" type="number" step="0.01" value={form.tds_amount} onChange={(e) => setForm({ ...form, tds_amount: e.target.value })} className="dos-input mt-1" /></div>
            <div><label className="text-xs text-neutral-500">GST</label><input data-testid="entry-gst" type="number" step="0.01" value={form.gst_amount} onChange={(e) => setForm({ ...form, gst_amount: e.target.value })} className="dos-input mt-1" /></div>
            <div><label className="text-xs text-neutral-500">Date</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="dos-input mt-1" /></div>
          </div>
          <input placeholder="Party (vendor / customer / payee)" value={form.party || ""} onChange={(e) => setForm({ ...form, party: e.target.value })} className="dos-input" />
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="dos-input">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <input placeholder="Description / narration" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="dos-input" />
          <CustomFieldsInputs module="finance" values={custom} onChange={setCustom} />
          {isNew && !record && (
            <div className="mt-2 rounded-md border border-neutral-200 bg-neutral-50 p-3">
              <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
                <input data-testid="entry-recurring" type="checkbox" checked={makeRecurring} onChange={(e) => setMakeRecurring(e.target.checked)} className="rounded" />
                <ArrowsClockwise size={14} className="text-neutral-500" />
                <span>Repeat automatically — draft a new pending entry every period</span>
              </label>
              {makeRecurring && (
                <div className="mt-2 flex items-center gap-2 pl-6">
                  <label className="text-xs text-neutral-500">Cadence</label>
                  <select data-testid="entry-cadence" value={cadence} onChange={(e) => setCadence(e.target.value)} className="dos-input py-1 text-xs" style={{ width: 140 }}>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  <span className="text-xs text-neutral-500">Starts from the next {cadence.replace(/ly$/, "")} period.</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
          <button data-testid="entry-save" type="submit" className="dos-btn dos-btn-primary">{isNew ? (record ? "Save Copy as New Entry" : "Save Entry") : "Update Entry"}</button>
        </div>
      </form>
    </div>
  );
}

const monthLabel = (key) => {
  if (!key) return "";
  const [y, m] = key.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(m, 10) - 1] || ""} '${String(y).slice(-2)}`;
};

function MonthlyChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-neutral-200 rounded-md shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-neutral-900 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2" style={{ color: p.color }}>
          <span className="capitalize">{p.dataKey}:</span>
          <span className="font-mono font-semibold">₹{Number(p.value || 0).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function MonthlyIncomeExpenseChart({ data, testId }) {
  const chartData = (data || []).map((m) => ({ ...m, label: monthLabel(m.month) }));
  return (
    <div className="dos-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <ChartLineUp size={14} className="text-neutral-500" />
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Monthly Income vs Expense</div>
        <div className="text-[10px] text-neutral-400 ml-2">All companies · selected financial year</div>
      </div>
      {chartData.length === 0 ? (
        <div className="py-16 text-center text-sm text-neutral-500">No entries yet for this period.</div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barGap={4}>
            <CartesianGrid vertical={false} stroke="#e5e5e5" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#737373" }} axisLine={{ stroke: "#e5e5e5" }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#737373" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} width={52} />
            <Tooltip content={<MonthlyChartTooltip />} cursor={{ fill: "#fafafa" }} />
            <Legend wrapperStyle={{ fontSize: 11, textTransform: "capitalize" }} iconType="circle" iconSize={8} />
            <Bar dataKey="income" name="Income" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={28} />
            <Bar dataKey="expense" name="Expense" fill="#f87171" radius={[3, 3, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ---------- Financial Reports Tab (FY P&L) ----------
export function ReportsTab() {
  const [fyStart, setFyStart] = useState(currentFY().start);
  const [companyId, setCompanyId] = useState("all");
  const [companies, setCompanies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [liability, setLiability] = useState(null);
  const [workspace, setWorkspace] = useState({});
  const fys = useMemo(() => listFYs(5), []);
  const fyLabel = fys.find((f) => f.start === fyStart)?.label || "";
  const [pageSize, setPageSize] = useState("A4");
  const activeCompany = companies.find((c) => c.id === companyId);
  const activeCompanyName = companyId === "all" ? "All Companies" : (activeCompany?.name || "");

  useEffect(() => {
    const params = new URLSearchParams({ fy_start: String(fyStart) });
    if (companyId !== "all") params.set("company_id", companyId);
    api.get(`/finance/summary?${params.toString()}`).then((r) => setSummary(r.data));
    api.get(`/finance/transfers/liability?${params.toString()}`).then((r) => setLiability(r.data)).catch(() => setLiability(null));
  }, [fyStart, companyId]);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/workspace/settings").then((r) => setWorkspace(r.data)).catch(() => {});
  }, []);

  const doPrint = () => {
    if (!summary) return;
    const html = buildReportHtml(summary, fyLabel, workspace, activeCompanyName, liability);
    printHtml(html, { title: `Financial Report ${fyLabel}${companyId !== "all" ? ` · ${activeCompanyName}` : ""}`, pageSize });
  };

  if (!summary) return <div className="p-12 text-center text-neutral-500 text-sm">Loading…</div>;
  const t = summary.totals;
  const patValue = (t.profit || 0) - ((t.gst_output || 0) - (t.gst_input || 0)) + ((t.tds_receivable || 0) - (t.tds_payable || 0));

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Financial Year</label>
        <select data-testid="reports-fy" value={fyStart} onChange={(e) => setFyStart(parseInt(e.target.value, 10))} className="dos-input" style={{ maxWidth: 160 }}>
          {fys.map((f) => <option key={f.start} value={f.start}>{f.label}</option>)}
        </select>
        <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 ml-2 inline-flex items-center gap-1">
          <Buildings size={12} /> Company
        </label>
        <select data-testid="reports-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="dos-input" style={{ maxWidth: 220 }}>
          <option value="all">All Companies</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {companyId !== "all" && (
          <span data-testid="reports-company-badge" className="dos-badge bg-neutral-900 text-white text-[10px]">Filtered</span>
        )}
        <div className="ml-auto flex gap-2 items-center">
          <select data-testid="reports-page-size" value={pageSize} onChange={(e) => setPageSize(e.target.value)} className="dos-input py-1 text-xs" style={{ width: 100 }}>
            <option>A4</option><option>Legal</option><option>Letter</option><option>A5</option>
          </select>
          <button data-testid="reports-print" onClick={doPrint} className="dos-btn dos-btn-primary"><Printer size={14} /> Print / PDF</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <MiniStat label="Income" value={t.income} tone="green" testId="stat-income" />
        <MiniStat label="Expense" value={t.expense} tone="red" testId="stat-expense" />
        <MiniStat label="Book Profit" value={t.profit} tone={t.profit >= 0 ? "green" : "red"} testId="stat-book-profit" />
        <MiniStat label="Profit After Tax" value={patValue} tone={patValue >= 0 ? "green" : "red"} testId="stat-pat" />
      </div>

      <div className="dos-card p-5 mb-6" data-testid="tax-outstanding-card">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-4">Tax &amp; Outstanding</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <InlineStat label="TDS Receivable" value={t.tds_receivable} tone="blue" />
          <InlineStat label="TDS Payable" value={t.tds_payable} tone="amber" />
          <InlineStat label="GST Output" value={t.gst_output} tone="amber" />
          <InlineStat label="GST Input" value={t.gst_input} tone="blue" />
          <InlineStat label="Net GST Liability" value={(t.gst_output || 0) - (t.gst_input || 0)} tone={((t.gst_output || 0) - (t.gst_input || 0)) >= 0 ? "red" : "green"} />
          <InlineStat label="Receivable" value={t.receivable} tone="neutral" />
          <InlineStat label="Payable" value={t.payable} tone="neutral" />
          <InlineStat label="Company Expenses" value={t.company_expense} tone="neutral" />
        </div>
      </div>

      <div className="mb-6">
        <MonthlyIncomeExpenseChart data={summary.by_month} testId="monthly-income-expense-chart" />
      </div>

      <div className="mb-6" data-testid="company-monthly-balance-section">
        <div className="flex items-center gap-2 mb-3">
          <Buildings size={14} className="text-neutral-500" />
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Company-wise Monthly Balance</div>
          <div className="text-[10px] text-neutral-400 ml-2">Income, expense &amp; internal transfers per company, carried forward month to month</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(summary.by_company_month || []).map((c) => (
            <CompanyMonthlyBalanceCard key={c.company_id} company={c} />
          ))}
          {(summary.by_company_month || []).length === 0 && (
            <div className="dos-card p-8 text-center text-sm text-neutral-500 md:col-span-2">No company activity yet for {fyLabel}.</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BreakdownCard title="Expense by Category" rows={summary.by_category_expense} />
        <BreakdownCard title="Income by Category" rows={summary.by_category_income} />
        <BreakdownCard title="Top Parties" rows={summary.by_party.map((r) => ({ category: r.party, amount: r.amount }))} />
        <div className="dos-card p-5 md:col-span-2" data-testid="reports-liability-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">Inter-Company Liability ({fyLabel})</div>
          {!liability || liability.pairs.length === 0 ? (
            <div className="py-4 text-center text-xs text-neutral-500">All company accounts are settled for this period.</div>
          ) : (
            <div className="space-y-1.5">
              {liability.pairs.map((p) => (
                <div key={`${p.debtor_id}-${p.creditor_id}`} className="flex items-center justify-between text-sm">
                  <div className="text-neutral-700"><span className="font-medium">{p.debtor_name}</span> owes <span className="font-medium">{p.creditor_name}</span></div>
                  <div className="font-mono font-semibold text-amber-700">₹{p.amount.toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
          <div className="text-[10px] text-neutral-400 mt-2">Excludes transfers marked <b>EMI Payment</b>. Manage and settle these on the Internal Transfers tab.</div>
        </div>
      </div>
    </div>
  );
}

function InlineStat({ label, value, tone = "neutral" }) {
  const cls = { green: "text-emerald-700", red: "text-red-700", amber: "text-amber-700", blue: "text-blue-700", neutral: "text-neutral-900" }[tone];
  return (
    <div>
      <div className="text-[10px] text-neutral-500 uppercase tracking-wide">{label}</div>
      <div className={`font-mono font-semibold mt-1 ${cls}`}>₹{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

function CompanyMonthlyBalanceCard({ company }) {
  return (
    <div className="dos-card p-5" data-testid={`company-balance-${company.company_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-neutral-800">{company.company_name}</div>
        <div className={`font-mono font-bold text-sm ${company.closing_balance >= 0 ? "text-emerald-700" : "text-red-700"}`}>
          Balance: ₹{company.closing_balance.toLocaleString()}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[520px]">
          <thead>
            <tr className="text-neutral-500 border-b border-neutral-100">
              <th className="text-left py-1.5">Month</th>
              <th className="text-right py-1.5">Income</th>
              <th className="text-right py-1.5">Expense</th>
              <th className="text-right py-1.5">Net</th>
              <th className="text-right py-1.5">Transfers</th>
              <th className="text-right py-1.5">Balance</th>
            </tr>
          </thead>
          <tbody>
            {company.months.map((m) => (
              <tr key={m.month} data-testid={`company-balance-row-${company.company_id}-${m.month}`} className="border-b border-neutral-100 last:border-0">
                <td className="py-1.5 text-neutral-700">{monthLabel(m.month)}</td>
                <td className="py-1.5 text-right font-mono text-emerald-700">₹{m.income.toLocaleString()}</td>
                <td className="py-1.5 text-right font-mono text-red-700">₹{m.expense.toLocaleString()}</td>
                <td className={`py-1.5 text-right font-mono ${m.net >= 0 ? "text-emerald-700" : "text-red-700"}`}>₹{m.net.toLocaleString()}</td>
                <td className="py-1.5 text-right font-mono text-neutral-500">{(m.transfers_in || m.transfers_out) ? `₹${(m.transfers_in - m.transfers_out).toLocaleString()}` : "—"}</td>
                <td className={`py-1.5 text-right font-mono font-semibold ${m.balance >= 0 ? "text-emerald-700" : "text-red-700"}`}>₹{m.balance.toLocaleString()}</td>
              </tr>
            ))}
            {company.months.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-neutral-400">No activity this period</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone = "neutral", testId }) {
  const cls = { green: "text-emerald-700", red: "text-red-700", amber: "text-amber-700", blue: "text-blue-700", neutral: "text-neutral-900" }[tone];
  return (
    <div className="dos-card p-4" data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`font-display text-lg font-bold mt-1 ${cls}`}>₹{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

function BreakdownCard({ title, rows }) {
  return (
    <div className="dos-card p-5">
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">{title}</div>
      <table className="w-full text-sm">
        <tbody>
          {(rows || []).map((r) => (
            <tr key={r.category} className="border-b border-neutral-100 last:border-0">
              <td className="py-1.5 text-neutral-700">{r.category}</td>
              <td className="py-1.5 text-right font-mono">₹{Number(r.amount || 0).toLocaleString()}</td>
            </tr>
          ))}
          {(rows || []).length === 0 && <tr><td colSpan={2} className="py-4 text-center text-xs text-neutral-500">No data</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function buildReportHtml(summary, fyLabel, workspace, companyLabel, liability) {
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  const t = summary.totals;
  const row = (label, val) => `<tr><td>${esc(label)}</td><td class="num font-mono">${currency(val)}</td></tr>`;
  const catRows = (rs) => (rs || []).map((r) => `<tr><td>${esc(r.category || r.party)}</td><td class="num font-mono">${currency(r.amount)}</td></tr>`).join("");
  return `
    <div class="section" style="border-bottom:1px solid #171717; padding-bottom:8px; margin-bottom:16px; display:flex; justify-content:space-between; align-items:flex-end;">
      <div>
        <div class="font-bold text-2xl">${esc(workspace?.name || "Gray Owl")}</div>
        <div class="text-xs text-neutral-500">${esc(workspace?.tagline || "")}</div>
      </div>
      <div style="text-align:right;">
        <div class="text-xs uppercase tracking-wider text-neutral-500">Financial Report</div>
        <div class="font-bold text-lg">${esc(fyLabel)}</div>
        <div class="text-xs text-neutral-500">Indian Financial Year (Apr–Mar)</div>
        ${companyLabel ? `<div class="text-xs text-neutral-700 mt-1"><b>Company:</b> ${esc(companyLabel)}</div>` : ""}
      </div>
    </div>
    <div class="section" style="margin-bottom:16px;">
      <div class="text-xs uppercase tracking-wider text-neutral-500 mb-2">Profit &amp; Loss Summary</div>
      <table class="items" style="width:100%;">
        <tbody>
          ${row("Total Income", t.income)}
          ${row("Total Expense", t.expense)}
          ${row("  — Project Expenses", t.project_expense)}
          ${row("  — Company Expenses", t.company_expense)}
          <tr style="font-weight:700; border-top:1px solid #171717;"><td>Net Profit</td><td class="num font-mono">${currency(t.profit)}</td></tr>
          ${row("Receivable (outstanding)", t.receivable)}
          ${row("Payable (outstanding)", t.payable)}
          ${row("TDS Deducted", t.tds)}
          ${row("GST", t.gst)}
        </tbody>
      </table>
    </div>
    <div class="section" style="margin-bottom:16px; display:grid; grid-template-columns:1fr 1fr; gap:16px;">
      <div>
        <div class="text-xs uppercase tracking-wider text-neutral-500 mb-2">Expense by Category</div>
        <table class="items" style="width:100%;"><thead><tr><th>Category</th><th class="num">Amount</th></tr></thead><tbody>${catRows(summary.by_category_expense)}</tbody></table>
      </div>
      <div>
        <div class="text-xs uppercase tracking-wider text-neutral-500 mb-2">Income by Category</div>
        <table class="items" style="width:100%;"><thead><tr><th>Category</th><th class="num">Amount</th></tr></thead><tbody>${catRows(summary.by_category_income)}</tbody></table>
      </div>
    </div>
    <div class="section" style="margin-bottom:16px;">
      <div class="text-xs uppercase tracking-wider text-neutral-500 mb-2">Top Parties (by amount)</div>
      <table class="items" style="width:100%;"><thead><tr><th>Party</th><th class="num">Amount</th></tr></thead><tbody>${catRows(summary.by_party)}</tbody></table>
    </div>
    <div class="section">
      <div class="text-xs uppercase tracking-wider text-neutral-500 mb-2">Company-wise Monthly Balance</div>
      ${(summary.by_company_month || []).map((c) => `
        <div style="margin-bottom:14px;">
          <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:4px;">
            <div class="font-bold" style="font-size:13px;">${esc(c.company_name)}</div>
            <div class="font-mono" style="font-size:12px;">Balance: ${currency(c.closing_balance)}</div>
          </div>
          <table class="items" style="width:100%;">
            <thead><tr><th>Month</th><th class="num">Income</th><th class="num">Expense</th><th class="num">Net</th><th class="num">Transfers</th><th class="num">Balance</th></tr></thead>
            <tbody>
              ${(c.months || []).map((m) => `<tr><td>${esc(monthLabel(m.month))}</td><td class="num font-mono">${currency(m.income)}</td><td class="num font-mono">${currency(m.expense)}</td><td class="num font-mono">${currency(m.net)}</td><td class="num font-mono">${currency((m.transfers_in || 0) - (m.transfers_out || 0))}</td><td class="num font-mono">${currency(m.balance)}</td></tr>`).join("") || `<tr><td colspan="6" style="text-align:center; color:#a3a3a3; padding:8px;">No activity this period</td></tr>`}
            </tbody>
          </table>
        </div>
      `).join("") || `<div style="text-align:center; color:#a3a3a3; padding:12px;">No company activity for this period.</div>`}
    </div>
    ${liability ? `<div class="section" style="margin-top:16px;">
      <div class="text-xs uppercase tracking-wider text-neutral-500 mb-2">Inter-Company Liability (FY-End)</div>
      <table class="items" style="width:100%;"><thead><tr><th>Debtor (owes)</th><th>Creditor (receives)</th><th class="num">Amount</th></tr></thead>
        <tbody>${liabilityTableRows(liability) || `<tr><td colspan="3" style="text-align:center; color:#a3a3a3; padding:12px;">All company accounts are settled for this period.</td></tr>`}</tbody>
      </table>
      <div class="text-xs" style="margin-top:6px; color:#737373;">Excludes transfers with purpose <b>EMI Payment</b>${liability.excluded_emi_total ? ` (${currency(liability.excluded_emi_total)} excluded)` : ""}.</div>
    </div>` : ""}
    <div class="section" style="margin-top:24px; font-size:10px; color:#a3a3a3; text-align:right;">Generated by ${esc(workspace?.name || "Gray Owl")} · ${new Date().toLocaleString()}</div>
  `;
}


function liabilityTableRows(liability) {
  return (liability?.pairs || []).map((p) => `<tr><td>${p.debtor_name}</td><td>${p.creditor_name}</td><td class="num font-mono">${currency(p.amount)}</td></tr>`).join("");
}

function buildLiabilityHtml(liability, fyLabel, workspace) {
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  const rows = liabilityTableRows(liability);
  return `
    <div class="section" style="border-bottom:1px solid #171717; padding-bottom:8px; margin-bottom:16px; display:flex; justify-content:space-between; align-items:flex-end;">
      <div>
        <div class="font-bold text-2xl">${esc(workspace?.name || "Gray Owl")}</div>
        <div class="text-xs text-neutral-500">${esc(workspace?.tagline || "")}</div>
      </div>
      <div style="text-align:right;">
        <div class="text-xs uppercase tracking-wider text-neutral-500">Inter-Company Liability Summary</div>
        <div class="font-bold text-lg">${esc(fyLabel)}</div>
        <div class="text-xs text-neutral-500">Indian Financial Year (Apr–Mar)</div>
      </div>
    </div>
    <div class="section">
      <table class="items" style="width:100%;">
        <thead><tr><th>Debtor (owes)</th><th>Creditor (receives)</th><th class="num">Amount</th></tr></thead>
        <tbody>${rows || `<tr><td colspan="3" style="text-align:center; color:#a3a3a3; padding:20px;">All company accounts are settled for this period.</td></tr>`}</tbody>
      </table>
    </div>
    <div class="section" style="margin-top:16px; font-size:11px; color:#737373;">
      Transfers with purpose <b>EMI Payment</b> are excluded from this calculation${liability?.excluded_emi_total ? ` (${currency(liability.excluded_emi_total)} excluded this period)` : ""}.
    </div>
    <div class="section" style="margin-top:24px; font-size:10px; color:#a3a3a3; text-align:right;">Generated by ${esc(workspace?.name || "Gray Owl")} · ${new Date().toLocaleString()}</div>
  `;
}

// ---------- Recurring Templates Tab ----------
function RecurringTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/templates");
      setTemplates(data);
    } catch (err) { toast.error(formatError(err)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (t) => {
    try {
      await api.patch(`/finance/templates/${t.id}`, { is_active: !t.is_active });
      load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const runNow = async (t) => {
    const ok = await confirm({ title: "Run template now?", message: `Insert a pending ${t.type} entry for "${t.name}" (₹${(t.amount || 0).toLocaleString()}) today?`, confirmLabel: "Run now" });
    if (!ok) return;
    try {
      await api.post(`/finance/templates/${t.id}/run-now`);
      toast.success("Draft entry inserted");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const del = async (t) => {
    const ok = await confirm({ title: "Delete recurring template?", message: `"${t.name}" will stop auto-drafting. Existing entries are kept.`, confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/finance/templates/${t.id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatError(err)); }
  };

  if (loading) return <div className="p-12 text-center text-neutral-500 text-sm">Loading…</div>;

  return (
    <div data-testid="finance-recurring-tab">
      <div className="mb-4 text-sm text-neutral-600">
        Templates below auto-draft a <span className="font-medium">Pending</span> finance entry every scheduled period. Admins are notified when a new draft is created — review and mark it Paid.
      </div>
      <div className="dos-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
              <th className="px-4 py-3">Template</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Cadence</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Next run</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50" data-testid={`rec-row-${t.id}`}>
                <td className="px-4 py-3 text-neutral-900">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-neutral-500">{t.category}{t.party ? ` · ${t.party}` : ""}</div>
                </td>
                <td className="px-4 py-3"><span className={`dos-badge ${t.type === "income" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{t.type}</span></td>
                <td className="px-4 py-3 text-neutral-700 capitalize">{t.cadence || "monthly"}</td>
                <td className="px-4 py-3 text-right font-mono">₹{(t.amount || 0).toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-500">{t.next_run || "—"}</td>
                <td className="px-4 py-3">
                  <button data-testid={`rec-toggle-${t.id}`} onClick={() => toggleActive(t)} className={`dos-badge cursor-pointer ${t.is_active === false ? "bg-neutral-100 text-neutral-500" : "bg-emerald-50 text-emerald-700"}`}>
                    {t.is_active === false ? "Paused" : "Active"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`rec-run-${t.id}`} onClick={() => runNow(t)} title="Run now — insert a pending entry today" className="p-1.5 rounded hover:bg-neutral-100"><Play size={14} /></button>
                  <button data-testid={`rec-pause-${t.id}`} onClick={() => toggleActive(t)} title={t.is_active === false ? "Resume" : "Pause"} className="p-1.5 rounded hover:bg-neutral-100"><Pause size={14} /></button>
                  <button data-testid={`rec-del-${t.id}`} onClick={() => del(t)} className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash size={14} /></button>
                </td>
              </tr>
            ))}
            {templates.length === 0 && (
              <tr><td colSpan={7} className="p-12 text-center text-neutral-500 text-sm">
                No recurring templates yet. When creating a new finance entry, tick <span className="font-medium">Repeat automatically</span> to save it as a recurring template.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


const TRANSFER_PURPOSES = ["Capital Support", "Working Capital", "EMI Payment", "Loan Repayment", "Expense Reimbursement", "Other"];

// ---------- Internal Transfers Tab ----------
export function TransfersTab() {
  const [transfers, setTransfers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [liability, setLiability] = useState(null);
  const [workspace, setWorkspace] = useState({});
  const [fyStart, setFyStart] = useState(currentFY().start);
  const [companyFilter, setCompanyFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [settling, setSettling] = useState(null);
  const confirm = useConfirm();
  const fys = useMemo(() => listFYs(5), []);
  const fyLabel = fys.find((f) => f.start === fyStart)?.label || "All periods";

  const load = async () => {
    const params = new URLSearchParams();
    if (fyStart !== "all") params.set("fy_start", String(fyStart));
    if (companyFilter !== "all") params.set("company_id", companyFilter);
    const [t, c, li, ws] = await Promise.all([
      api.get(`/finance/transfers?${params.toString()}`),
      api.get("/companies"),
      api.get(`/finance/transfers/liability?${params.toString()}`),
      api.get("/workspace/settings").catch(() => ({ data: {} })),
    ]);
    setTransfers(t.data); setCompanies(c.data); setLiability(li.data); setWorkspace(ws.data || {});
  };
  useEffect(() => { load(); }, [fyStart, companyFilter]);

  const totalValue = transfers.reduce((s, t) => s + (t.amount || 0), 0);
  const totalIn  = companyFilter === "all" ? totalValue : transfers.filter((t) => t.to_company_id === companyFilter).reduce((s, t) => s + (t.amount || 0), 0);
  const totalOut = companyFilter === "all" ? totalValue : transfers.filter((t) => t.from_company_id === companyFilter).reduce((s, t) => s + (t.amount || 0), 0);

  const del = async (t) => {
    const ok = await confirm({ title: "Delete transfer?", message: `Delete transfer of ₹${(t.amount || 0).toLocaleString()} on ${t.date}?`, confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/finance/transfers/${t.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const settle = async (p) => {
    const ok = await confirm({
      title: "Mark liability as settled?",
      message: `This records an offsetting transfer of ₹${p.amount.toLocaleString()} from ${p.debtor_name} to ${p.creditor_name}, bringing this pair's balance to ₹0 for ${fyLabel}.`,
      confirmLabel: "Mark Settled",
    });
    if (!ok) return;
    setSettling(`${p.debtor_id}-${p.creditor_id}`);
    try {
      await api.post("/finance/transfers", {
        date: new Date().toISOString().slice(0, 10),
        amount: p.amount,
        from_company_id: p.debtor_id,
        to_company_id: p.creditor_id,
        mode: "Bank Transfer",
        purpose: "Loan Repayment",
        note: `Settlement of ${fyLabel} inter-company liability`,
      });
      toast.success("Liability settled — offsetting transfer recorded");
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSettling(null); }
  };

  const doPrintLiability = () => {
    if (!liability) return;
    const html = buildLiabilityHtml(liability, fyLabel, workspace);
    printHtml(html, { title: `Inter-Company Liability — ${fyLabel}`, pageSize: "A4" });
  };

  return (
    <div data-testid="transfers-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Financial Year</label>
        <select data-testid="transfers-fy" value={fyStart} onChange={(e) => setFyStart(e.target.value === "all" ? "all" : parseInt(e.target.value, 10))} className="dos-input" style={{ maxWidth: 160 }}>
          <option value="all">All periods</option>
          {fys.map((f) => <option key={f.start} value={f.start}>{f.label}</option>)}
        </select>
        <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 ml-3 inline-flex items-center gap-1"><Buildings size={12} /> Company</label>
        <select data-testid="transfers-company" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} className="dos-input" style={{ maxWidth: 220 }}>
          <option value="all">All Companies</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="ml-auto">
          <button data-testid="new-transfer" onClick={() => { setEdit(null); setOpen(true); }} className="dos-btn dos-btn-primary"><Plus size={14} /> New Transfer</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MiniStat label="Total Transfers" value={totalValue} tone="neutral" testId="tr-total" />
        <MiniStat label="Count" value={transfers.length} tone="neutral" />
        {companyFilter !== "all" ? <>
          <MiniStat label="Inflow (this company)" value={totalIn} tone="green" testId="tr-in" />
          <MiniStat label="Outflow (this company)" value={totalOut} tone="red" testId="tr-out" />
        </> : <>
          <MiniStat label="Note" value={0} tone="neutral" />
          <MiniStat label="P&L impact" value={0} tone="neutral" />
        </>}
      </div>
      <div className="text-[11px] text-neutral-500 -mt-3 mb-4">Internal transfers are P&amp;L-neutral — they do not change total income, expense or net profit. They only affect per-company cashflow.</div>

      <div className="dos-card p-5 mb-6" data-testid="liability-summary-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Financial Year-End Liability Summary</div>
            <div className="text-[11px] text-neutral-400 mt-0.5">Net of transfers between company pairs · {fyLabel}</div>
          </div>
          <button data-testid="print-liability" onClick={doPrintLiability} disabled={!liability || !liability.pairs.length} className="dos-btn dos-btn-secondary text-xs disabled:opacity-40 disabled:cursor-not-allowed"><Printer size={12} /> Print / PDF</button>
        </div>
        {!liability ? (
          <div className="py-6 text-center text-sm text-neutral-500">Loading…</div>
        ) : liability.pairs.length === 0 ? (
          <div className="py-6 text-center text-sm text-neutral-500">All company accounts are settled for this period.</div>
        ) : (
          <div className="space-y-2">
            {liability.pairs.map((p, i) => {
              const key = `${p.debtor_id}-${p.creditor_id}`;
              return (
                <div key={key} data-testid={`liability-row-${i}`} className="flex items-center justify-between px-3 py-2.5 rounded-md bg-amber-50 border border-amber-100">
                  <div className="text-sm text-neutral-800">
                    <span className="font-semibold">{p.debtor_name}</span> owes <span className="font-semibold">{p.creditor_name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="font-mono font-bold text-amber-800">₹{p.amount.toLocaleString()}</div>
                    <button
                      data-testid={`settle-liability-${i}`}
                      onClick={() => settle(p)}
                      disabled={settling === key}
                      title="Record an offsetting transfer to zero out this balance"
                      className="dos-btn dos-btn-secondary text-xs disabled:opacity-50"
                    >
                      {settling === key ? "Settling…" : "Mark Settled"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="text-[10px] text-neutral-400 mt-3">
          Transfers with purpose <b>EMI Payment</b> are excluded from this liability calculation
          {liability?.excluded_emi_total ? ` (₹${liability.excluded_emi_total.toLocaleString()} excluded this period)` : ""}.
        </div>
      </div>

      <div className="dos-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">From</th>
              <th className="px-4 py-3"></th>
              <th className="px-4 py-3">To</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Mode / Purpose</th>
              <th className="px-4 py-3">Reference / Note</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {transfers.map((t) => (
              <tr key={t.id} data-testid={`transfer-row-${t.id}`} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50">
                <td className="px-4 py-3 font-mono text-xs">{t.date}</td>
                <td className="px-4 py-3"><span className="dos-badge bg-red-50 text-red-700">{t.from_company_name || "—"}</span></td>
                <td className="px-4 py-3 text-center text-neutral-400"><ArrowsLeftRight size={14} /></td>
                <td className="px-4 py-3"><span className="dos-badge bg-emerald-50 text-emerald-700">{t.to_company_name || "—"}</span></td>
                <td className="px-4 py-3 text-right font-mono font-semibold">₹{(t.amount || 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-xs text-neutral-600">
                  <div>{t.mode || "—"}</div>
                  {(t.purpose || "Capital Support").toLowerCase().includes("emi")
                    ? <span className="dos-badge bg-amber-50 text-amber-700 mt-1 inline-block">EMI · excluded</span>
                    : <div className="text-neutral-400">{t.purpose || "Capital Support"}</div>}
                </td>
                <td className="px-4 py-3 text-xs text-neutral-600">
                  {t.reference_no && <div className="font-mono">{t.reference_no}</div>}
                  {t.note && <div className="text-neutral-500 truncate max-w-xs">{t.note}</div>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`edit-transfer-${t.id}`} onClick={() => { setEdit(t); setOpen(true); }} className="p-1.5 rounded hover:bg-neutral-100"><PencilSimple size={12} /></button>
                    <button data-testid={`del-transfer-${t.id}`} onClick={() => del(t)} className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {transfers.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-neutral-500">No internal transfers yet. Click <b>New Transfer</b> to log money moved between company accounts.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && <TransferModal record={edit} companies={companies} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function TransferModal({ record, companies, onClose, onSaved }) {
  const isEdit = !!record;
  const [form, setForm] = useState(record ? {
    date: record.date || new Date().toISOString().slice(0, 10),
    amount: record.amount || 0,
    from_company_id: record.from_company_id || "",
    to_company_id: record.to_company_id || "",
    mode: record.mode || "Bank Transfer",
    purpose: record.purpose || "Capital Support",
    reference_no: record.reference_no || "",
    note: record.note || "",
  } : {
    date: new Date().toISOString().slice(0, 10),
    amount: 0,
    from_company_id: companies[0]?.id || "",
    to_company_id: companies[1]?.id || "",
    mode: "Bank Transfer",
    purpose: "Capital Support",
    reference_no: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.from_company_id || !form.to_company_id) { toast.error("Pick both source and destination companies"); return; }
    if (form.from_company_id === form.to_company_id) { toast.error("Source and destination must be different companies"); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error("Amount must be greater than 0"); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (isEdit) await api.patch(`/finance/transfers/${record.id}`, payload);
      else await api.post("/finance/transfers", payload);
      toast.success(isEdit ? "Transfer updated" : "Transfer recorded");
      onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 overflow-y-auto p-4 flex items-start justify-center" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-lg my-8 fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div>
            <div className="font-display font-semibold text-neutral-900">{isEdit ? "Edit" : "New"} Internal Transfer</div>
            <div className="text-[11px] text-neutral-500">Move money between your own company accounts. P&amp;L neutral.</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-neutral-500">Date</label>
              <input data-testid="tr-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="dos-input mt-1" />
            </div>
            <div>
              <label className="text-xs text-neutral-500">Amount (₹)</label>
              <input data-testid="tr-amount" type="number" min="0" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="dos-input mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-neutral-500">From (Source Company)</label>
              <select data-testid="tr-from" required value={form.from_company_id} onChange={(e) => setForm({ ...form, from_company_id: e.target.value })} className="dos-input mt-1">
                <option value="">Select…</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-neutral-500">To (Destination Company)</label>
              <select data-testid="tr-to" required value={form.to_company_id} onChange={(e) => setForm({ ...form, to_company_id: e.target.value })} className="dos-input mt-1">
                <option value="">Select…</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-neutral-500">Mode</label>
              <select data-testid="tr-mode" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="dos-input mt-1">
                <option>Bank Transfer</option><option>RTGS / NEFT</option><option>UPI</option><option>Cheque</option><option>Cash</option><option>Other</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-neutral-500">Reference / UTR</label>
              <input data-testid="tr-ref" value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} className="dos-input mt-1" placeholder="e.g. UTR / cheque no." />
            </div>
          </div>
          <div>
            <label className="text-xs text-neutral-500">Purpose</label>
            <select data-testid="tr-purpose" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="dos-input mt-1">
              {TRANSFER_PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            {form.purpose === "EMI Payment" && (
              <div className="text-[11px] text-amber-700 mt-1">This transfer will be excluded from the FY-end liability summary.</div>
            )}
          </div>
          <div>
            <label className="text-xs text-neutral-500">Note</label>
            <textarea data-testid="tr-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="dos-input mt-1" placeholder="e.g. Capital top-up, working capital move" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
          <button data-testid="tr-save" type="submit" disabled={saving} className="dos-btn dos-btn-primary">{saving ? "Saving…" : (isEdit ? "Update Transfer" : "Record Transfer")}</button>
        </div>
      </form>
    </div>
  );
}
